<template>
  <div id="app" class="min-vh-100 d-flex flex-column">
    <!-- Skip link for keyboard navigation (Story 2.7a) -->
    <a
      href="#main-content"
      class="skip-link visually-hidden-focusable"
      @click.prevent="skipToMain"
    >
      {{ $t('accessibility.skip_to_content') }}
    </a>

    <!-- Navbar for authenticated users -->
    <AppNavbar v-if="authStore.isAuthenticated" />

    <!-- Main content -->
    <main id="main-content" class="flex-grow-1" tabindex="-1" role="main">
      <router-view />
    </main>

    <!-- Footer -->
    <footer v-if="authStore.isAuthenticated" class="bg-light py-3 mt-auto" role="contentinfo">
      <div class="container text-center text-muted small">
        &copy; {{ currentYear }} Comité Départemental de Billard 54
      </div>
    </footer>

    <!-- PWA Components (Story 4.1-4.4) -->
    <OfflineIndicator />
    <UpdateNotification />
    <InstallPrompt />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useAccessibility } from '@/composables/useAccessibility';
import AppNavbar from '@/components/Layout/AppNavbar.vue';
import OfflineIndicator from '@/components/Pwa/OfflineIndicator.vue';
import UpdateNotification from '@/components/Pwa/UpdateNotification.vue';
import InstallPrompt from '@/components/Pwa/InstallPrompt.vue';

const authStore = useAuthStore();
const { initialize } = useAccessibility();
const currentYear = computed(() => new Date().getFullYear());

// Initialize accessibility settings on mount
onMounted(() => {
  initialize();
});

// Skip to main content handler
function skipToMain(): void {
  const main = document.getElementById('main-content');
  if (main) {
    main.focus();
    main.scrollIntoView({ behavior: 'smooth' });
  }
}
</script>

<style>
/* Skip link styles (Story 2.7a) */
.skip-link {
  position: absolute;
  top: 0;
  left: 0;
  background: var(--bs-primary);
  color: white;
  padding: 0.5rem 1rem;
  z-index: 9999;
  text-decoration: none;
}

.skip-link:focus {
  position: fixed;
  top: 0;
  left: 0;
}

/* Focus visible styles (Story 2.7a) */
:focus-visible {
  outline: 2px solid var(--bs-primary);
  outline-offset: 2px;
}

/* Reduced motion (Story 2.7d) */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* Main content focus style for skip link */
#main-content:focus {
  outline: none;
}
</style>
